// src/components/Chatbot/Chatbot.jsx
import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import {
  FaPaperPlane,
  FaSpinner,
  FaChevronDown,
  FaCalendarCheck,
  FaClipboardList,
  FaEdit,
  FaTimesCircle,
  FaUsers,
  FaTag,
  FaClock,
  FaBed,
  FaMoon,
  FaPhone,
  FaUser,
  FaCheckCircle,
  FaHashtag,
  FaEnvelope,
  FaMoneyBillWave,
  FaHeart,
  FaMicrophone,
  FaStop,
  FaVolumeUp,
  FaVolumeMute,
} from "react-icons/fa";
import "./chatbot.css";

/**
 * NetShine ONE – Hotel Booking AI Chatbot (Widget-ready)
 * - Themeable via `themeColor` prop (sets CSS var --primary)
 * - Consumes aiBot config: assistant name + quickActionKeys
 */

const SUPPORT_AGENTS = [
  { name: "Nancy", image: "https://netshine-assets.blr1.cdn.digitaloceanspaces.com/app-assets/ns-bot1.png" },
  { name: "Jiya",  image: "https://netshine-assets.blr1.cdn.digitaloceanspaces.com/app-assets/ns-bot.png"  },
];

const DEFAULT_AGENT_IMAGE = SUPPORT_AGENTS?.[0]?.image || "https://netshine-assets.blr1.cdn.digitaloceanspaces.com/app-assets/ns-bot.png";

const SESSION_KEY      = "agenticChatSession_v2";
const SESSION_ID_KEY   = "agenticChatSessionId_v2";
const LAST_ACTIVE_KEY  = "agenticChatLastActive_v2";
const BOT_ENDPOINT     = "/booking-engine/ai/chat";
const BOT_TRANSCRIBE_ENDPOINT = "/booking-engine/ai/chat/transcribe";
const BOT_TTS_ENDPOINT = "/booking-engine/ai/chat/tts";
const IDLE_RESET_MS   = 5 * 60 * 1000;   // 5 minutes
const OTP_GRACE_MS    = 15 * 60 * 1000;  // 15 minutes grace when OTP pending

// Allowed quick actions coming from bootstrap
const ALLOWED_QA = [
  "availability",
  "book",
  "myBookings",
  "modify",
  "cancel",
  "offers",
  "eventsWedding",
];

// ──────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────
const isPlainObject = (v) => v && typeof v === "object" && !Array.isArray(v);

const parseCommaNames = (s) =>
  String(s || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

const sanitizeKeys = (keys) =>
  Array.from(
    new Set(
      (Array.isArray(keys) ? keys : [])
        .map((k) => String(k || "").trim())
        .filter((k) => ALLOWED_QA.includes(k))
    )
  );

const resolveAgentImage = (name) => {
  const n = String(name || "").trim().toLowerCase();
  const hit = SUPPORT_AGENTS.find((a) => String(a?.name || "").trim().toLowerCase() === n);
  return hit?.image || DEFAULT_AGENT_IMAGE;
};

const pickOneRandom = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  return arr[Math.floor(Math.random() * arr.length)];
};

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

const getUsername = () => localStorage.getItem("username") || "there";

const joinUrl = (base, path) =>
  `${String(base || "").replace(/\/+$/, "")}/${String(path || "").replace(/^\/+/, "")}`;

const formatTimestamp = (ts) =>
  new Date(ts).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

const newSessionId = () => {
  try {
    const w = typeof window !== "undefined" ? window : null;
    const c = w && w.crypto ? w.crypto : null;
    if (c && typeof c.randomUUID === "function") return c.randomUUID();
  } catch {}
  return `sess_${Date.now()}`;
};

const touchActivity = () => localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now()));
const getLastActive = () => Number(localStorage.getItem(LAST_ACTIVE_KEY) || "0");

// Asset base for room-type images returned by backend (rooms_list UI)
const ROOM_ASSET_BASE = "https://netshine-assets.blr1.cdn.digitaloceanspaces.com";
const isAbsUrl = (u) => /^https?:\/\//i.test(String(u || ""));

const formatINR = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);
  } catch {
    return `₹${Math.round(n)}`;
  }
};

const formatMoney = (amount, currency = "INR") => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return currency === "INR" ? `₹${Math.round(n)}` : `${n}`;
  }
};


const getSpeechRecognitionCtor = () => {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

const cleanTextForSpeech = (text) =>
  String(text || "")
    .replace(/\*\*/g, "")
    .replace(/`/g, "")
    .replace(/\s+/g, " ")
    .trim();

// ──────────────────────────────────────────────
// Formatters
// ──────────────────────────────────────────────
const formatDateHuman = (d) => {
  if (!d) return "—";
  const date = new Date(`${String(d).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, { weekday:"short", day:"2-digit", month:"short", year:"numeric" });
};

const normalizeStatus = (s) => String(s || "").toLowerCase();

const statusLabel = (s) => {
  const x = normalizeStatus(s);
  if (x === "confirmed") return "Confirmed";
  if (x === "pending")   return "Pending";
  if (x === "canceled" || x === "cancelled") return "Cancelled";
  if (x === "no_show" || x === "no-show")    return "No-show";
  if (x === "completed") return "Completed";
  if (x === "reserved")  return "Reserved";
  return s || "—";
};

const statusClass = (s) => {
  const x = normalizeStatus(s);
  if (x === "confirmed" || x === "reserved") return "ns-status-confirmed";
  if (x === "pending")   return "ns-status-pending";
  if (x === "canceled" || x === "cancelled") return "ns-status-cancelled";
  return "ns-status";
};

// ──────────────────────────────────────────────
// Quick actions defs (keys MUST match backend/bootstrap keys)
// ──────────────────────────────────────────────
const QUICK_ACTION_DEFS = [
  { key: "availability", label: "Check availability", text: "Check room availability", icon: <FaClock /> },
  { key: "book",         label: "Make booking",       text: "Make a new booking",        icon: <FaCalendarCheck /> },
  { key: "myBookings",   label: "My bookings",        text: "Show my bookings",          icon: <FaClipboardList /> },
  { key: "modify",       label: "Modify booking",     text: "Modify my booking",         icon: <FaEdit /> },
  { key: "cancel",       label: "Cancel booking",     text: "Cancel my booking",         icon: <FaTimesCircle /> },
  { key: "offers",       label: "Offers",             text: "Show current offers",       icon: <FaTag /> },
  { key: "eventsWedding",label: "Events & Wedding",   text: "I want to enquire about events & wedding", icon: <FaHeart /> },
];

const QUICK_ACTION_TEXT_HINT = {
  availability: "check availability",
  book: "create a booking",
  myBookings: "view your bookings",
  modify: "modify a booking",
  cancel: "cancel a booking",
  offers: "see current offers",
  eventsWedding: "events & wedding enquiries",
};

const buildWelcomeText = (agentName, enabledKeys) => {
  const keys = Array.isArray(enabledKeys) ? enabledKeys : [];
  const phrases = keys.map((k) => QUICK_ACTION_TEXT_HINT[k]).filter(Boolean);
  const list = phrases.length ? phrases : ["check availability", "create a booking", "view/modify/cancel bookings", "see offers"];

  return (
`${getGreeting()}, ${getUsername()}!

I'm ${agentName}, your Front Office Assistant.
You can ${list.join(", ")}.`
  );
};

// ──────────────────────────────────────────────
// Components
// ──────────────────────────────────────────────
const QuickActions = ({ onPick, disabled = false, actionKeys = [] }) => {
  const byKey = useMemo(() => {
    const m = {};
    QUICK_ACTION_DEFS.forEach((a) => { m[a.key] = a; });
    return m;
  }, []);

  const ordered = useMemo(() => {
    const keys = sanitizeKeys(actionKeys);
    // Keep user-selected ordering if provided; fallback to default order
    if (keys.length > 0) return keys.map((k) => byKey[k]).filter(Boolean);
    return QUICK_ACTION_DEFS;
  }, [actionKeys, byKey]);

  if (!ordered.length) return null;

  return (
    <div className="quick-actions-container">
      <div className="quick-actions-grid">
        {ordered.map((a) => (
          <button
            key={a.key}
            type="button"
            className="quick-action-card"
            disabled={disabled}
            onClick={() => onPick(a.text)}
            aria-label={a.label}
          >
            <div className="quick-action-icon">{a.icon}</div>
            <div className="quick-action-text">
              <div className="quick-action-label">{a.label}</div>
              <div className="quick-action-hint">Tap to begin</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const BookingsPanel = ({ data = [], onAction }) => {
  if (!Array.isArray(data) || data.length === 0) return null;

  return (
    <div className="panel bookings-panel">
      <div className="panel-header">
        <div className="panel-title">Your Bookings</div>
        <div className="panel-count">{data.length}</div>
      </div>

      <div className="panel-body">
        {data.map((b, idx) => {
          const id = String(b.bookingId ?? b.id ?? "");
          const ref = String(b.bookingNumber ?? "");
          const status = b.status || b.bookingType || "—";

          const checkIn = b.checkInLabel || formatDateHuman(String(b.checkInDate || "").slice(0, 10));
          const checkOut = b.checkOutLabel || formatDateHuman(String(b.checkOutDate || "").slice(0, 10));
          const nights = Number(b.nights ?? 0) || 0;

          const guest = b.guestName || "—";
          const phone = b.guestPhone || "—";
          const email = b.guestEmail || "—";

          const roomType = b.roomType || b.room_type || b.room_type_name || b.room_type_label || "—";
          const adults = Number(b.adults ?? 0) || 0;
          const roomsCount = Number(b.roomsCount ?? b.rooms_count ?? b.rooms ?? 1) || 1;

          const currency = b.currency || "INR";
          const total = formatMoney(b.totalAmount ?? b.amountDue ?? 0, currency);
          const due = formatMoney(b.amountDue ?? b.totalAmount ?? 0, currency);

          const key = ref || id || `${idx}_${checkIn}_${checkOut}`;
          const canAct = !!(ref || id);
          const actionKey = ref || id;

          return (
            <div key={key} className="booking-card">
              <div className="booking-top">
                <div className="booking-ref">
                  <div className="booking-ref-main">{ref || "Booking"}</div>
                  <div className="booking-ref-sub">
                    ID: {id || "—"} • {guest} • {phone}
                  </div>
                </div>
                <div className={statusClass(status)}>{statusLabel(status)}</div>
              </div>

              <div className="booking-grid">
                <div className="booking-item">
                  <div className="booking-ic"><FaCalendarCheck /></div>
                  <div className="booking-kv">
                    <div className="booking-k">Check-in</div>
                    <div className="booking-v">{checkIn}</div>
                  </div>
                </div>

                <div className="booking-item">
                  <div className="booking-ic"><FaCalendarCheck /></div>
                  <div className="booking-kv">
                    <div className="booking-k">Check-out</div>
                    <div className="booking-v">{checkOut}</div>
                  </div>
                </div>

                <div className="booking-item">
                  <div className="booking-ic"><FaMoon /></div>
                  <div className="booking-kv">
                    <div className="booking-k">Nights</div>
                    <div className="booking-v">{nights || "—"}</div>
                  </div>
                </div>

                <div className="booking-item">
                  <div className="booking-ic"><FaBed /></div>
                  <div className="booking-kv">
                    <div className="booking-k">Room</div>
                    <div className="booking-v">{roomType}</div>
                  </div>
                </div>

                <div className="booking-item">
                  <div className="booking-ic"><FaUsers /></div>
                  <div className="booking-kv">
                    <div className="booking-k">Guests</div>
                    <div className="booking-v">{adults ? `${adults} adult${adults > 1 ? "s" : ""}` : "—"}</div>
                  </div>
                </div>

                <div className="booking-item">
                  <div className="booking-ic"><FaClipboardList /></div>
                  <div className="booking-kv">
                    <div className="booking-k">Rooms</div>
                    <div className="booking-v">{roomsCount}</div>
                  </div>
                </div>
              </div>

              <div className="booking-money">
                <div className="money-item">
                  <div className="money-label">Total</div>
                  <div className="money-value">{total}</div>
                </div>
                <div className="money-item">
                  <div className="money-label">Due</div>
                  <div className="money-value">{due}</div>
                </div>
              </div>

              <div className="booking-actions">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => onAction(`MODIFY ${actionKey}`)}
                  disabled={!canAct}
                >
                  Modify
                </button>
                <button
                  type="button"
                  className="btn btn-danger-outline"
                  onClick={() => onAction(`CANCEL ${actionKey}`)}
                  disabled={!canAct}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => onAction("Check room availability")}
                >
                  New dates
                </button>
              </div>

              <div className="booking-mini">
                <span><FaEnvelope /> {email}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bookings-hint">
        Tip: You can also type <b>CANCEL &lt;bookingRef&gt;</b> or <b>MODIFY &lt;bookingRef&gt;</b>
      </div>
    </div>
  );
};

// (rest of your panels unchanged)
const BookingPreviewPanel = ({ meta, onAction }) => {
  const ui = meta?.data?.ui;
  const stage = String(meta?.stage || "");
  const isPreview = !!ui?.preview || stage === "AWAITING_CONFIRM";
  if (!isPreview) return null;

  const e = meta?.entities || {};
  const room = e.room_type || "—";
  const checkIn = e.check_in_date || null;
  const checkOut = e.check_out_date || null;
  const adults = Number(e.adults ?? 1) || 1;
  const roomsCount = Number(e.rooms_count ?? 1) || 1;
  const guestName = e.guest_name || "—";
  const phone = e.phone || "—";

  let nights = null;
  try {
    if (checkIn && checkOut) {
      const a = new Date(`${String(checkIn).slice(0, 10)}T00:00:00`);
      const b = new Date(`${String(checkOut).slice(0, 10)}T00:00:00`);
      const diff = Math.round((b - a) / (1000 * 60 * 60 * 24));
      nights = Number.isFinite(diff) && diff > 0 ? diff : null;
    }
  } catch {
    nights = null;
  }

  const rows = [
    { icon: <FaBed />, label: "Room", value: room },
    { icon: <FaCalendarCheck />, label: "Check-in", value: formatDateHuman(checkIn) },
    { icon: <FaCalendarCheck />, label: "Check-out", value: formatDateHuman(checkOut) },
    { icon: <FaMoon />, label: "Nights", value: nights != null ? String(nights) : "—" },
    { icon: <FaUsers />, label: "Adults", value: `${adults}` },
    { icon: <FaClipboardList />, label: "Rooms", value: String(roomsCount) },
    { icon: <FaUser />, label: "Name", value: guestName },
    { icon: <FaPhone />, label: "Phone", value: phone },
  ];

  return (
    <div className="panel preview-panel">
      <div className="panel-header">
        <div className="panel-title">Booking Preview</div>
        <div className="panel-pill preview-pill">
          <FaCheckCircle style={{ marginRight: 6 }} />
          Review
        </div>
      </div>

      <div className="preview-body">
        <div className="preview-grid">
          {rows.map((r) => (
            <div key={r.label} className="preview-row">
              <div className="preview-icon">{r.icon}</div>
              <div className="preview-content">
                <div className="preview-label">{r.label}</div>
                <div className="preview-value">{r.value}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="preview-note">
          Please review your details. If everything looks good, tap <b>Confirm</b>.
          I’ll then send OTP on your email.
        </div>

        <div className="preview-actions">
          <button type="button" className="btn btn-success" onClick={() => onAction("YES")}>
            Confirm
          </button>
          <button type="button" className="btn btn-outline" onClick={() => onAction("Change dates")}>
            Change dates
          </button>
          <button type="button" className="btn btn-outline" onClick={() => onAction("Change guests")}>
            Change guests
          </button>
          <button type="button" className="btn btn-outline" onClick={() => onAction("Change name")}>
            Change name
          </button>
          <button type="button" className="btn btn-outline" onClick={() => onAction("Change phone")}>
            Change phone
          </button>
        </div>
      </div>
    </div>
  );
};

const BookingConfirmedPanel = ({ meta, onAction }) => {
  const intent = String(meta?.intent || "").toLowerCase();
  if (intent !== "book" || !meta?.action_executed) return null;

  const bookingNumber = meta?.data?.bookingNumber || meta?.data?.booking_ref || meta?.data?.bookingRef || null;
  const bookingId = meta?.data?.bookingId || meta?.data?.booking_id || null;
  const s = meta?.data?.summary || meta?.data?.booking?.summary || null;
  if (!bookingNumber && !bookingId && !s) return null;

  const guest = s?.guest_name || "—";
  const phone = s?.phone || "—";
  const email = s?.email || "—";
  const checkIn = s?.check_in_date || null;
  const checkOut = s?.check_out_date || null;
  const nights = s?.nights ?? null;
  const room = s?.room_type || "—";
  const adults = s?.adults ?? "—";
  const roomsCount = s?.rooms_count ?? "—";

  const totals = s?.totals || {};
  const subtotal = totals?.subtotal ?? null;
  const taxes = totals?.taxes ?? null;
  const total = totals?.total ?? null;

  const chips = [
    bookingNumber ? { icon: <FaHashtag />, label: "Booking Ref", value: bookingNumber } : null,
    bookingId ? { icon: <FaHashtag />, label: "Booking ID", value: String(bookingId) } : null,
  ].filter(Boolean);

  return (
    <div className="panel confirm-panel">
      <div className="panel-header">
        <div className="panel-title">Booking Confirmed</div>
        <div className="panel-pill confirm-pill">
          <FaCheckCircle style={{ marginRight: 6 }} />
          Confirmed
        </div>
      </div>

      <div className="confirm-body">
        {chips.length > 0 && (
          <div className="confirm-chips">
            {chips.map((c) => (
              <div key={c.label} className="confirm-chip">
                <span className="confirm-chip-ic">{c.icon}</span>
                <span className="confirm-chip-lbl">{c.label}:</span>
                <span className="confirm-chip-val">{c.value}</span>
              </div>
            ))}
          </div>
        )}

        <div className="confirm-grid">
          <div className="confirm-row"><div className="confirm-ic"><FaUser /></div><div><div className="confirm-label">Guest</div><div className="confirm-value">{guest}</div></div></div>
          <div className="confirm-row"><div className="confirm-ic"><FaPhone /></div><div><div className="confirm-label">Phone</div><div className="confirm-value">{phone}</div></div></div>
          <div className="confirm-row"><div className="confirm-ic"><FaEnvelope /></div><div><div className="confirm-label">Email</div><div className="confirm-value">{email}</div></div></div>

          <div className="confirm-row"><div className="confirm-ic"><FaCalendarCheck /></div><div><div className="confirm-label">Check-in</div><div className="confirm-value">{formatDateHuman(checkIn)}</div></div></div>
          <div className="confirm-row"><div className="confirm-ic"><FaCalendarCheck /></div><div><div className="confirm-label">Check-out</div><div className="confirm-value">{formatDateHuman(checkOut)}</div></div></div>
          <div className="confirm-row"><div className="confirm-ic"><FaMoon /></div><div><div className="confirm-label">Nights</div><div className="confirm-value">{nights ?? "—"}</div></div></div>

          <div className="confirm-row"><div className="confirm-ic"><FaBed /></div><div><div className="confirm-label">Room</div><div className="confirm-value">{room}</div></div></div>
          <div className="confirm-row"><div className="confirm-ic"><FaUsers /></div><div><div className="confirm-label">Adults</div><div className="confirm-value">{adults}</div></div></div>
          <div className="confirm-row"><div className="confirm-ic"><FaClipboardList /></div><div><div className="confirm-label">Rooms</div><div className="confirm-value">{roomsCount}</div></div></div>
        </div>

        {(subtotal != null || taxes != null || total != null) && (
          <div className="confirm-total">
            <div className="confirm-total-row">
              <span>Subtotal</span>
              <span>{formatINR(subtotal)}</span>
            </div>
            <div className="confirm-total-row">
              <span>Taxes</span>
              <span>{formatINR(taxes)}</span>
            </div>
            <div className="confirm-total-row total">
              <span><FaMoneyBillWave style={{ marginRight: 6 }} /> Total</span>
              <span>{formatINR(total)}</span>
            </div>
            <div className="confirm-total-note">Includes tax (as per booking summary).</div>
          </div>
        )}

        <div className="confirm-actions">
          <button type="button" className="btn btn-primary" onClick={() => onAction("Show my bookings")}>
            View My Bookings
          </button>
          <button type="button" className="btn btn-outline" onClick={() => onAction("Check room availability")}>
            Check Availability
          </button>
        </div>
      </div>
    </div>
  );
};

const RoomsListPanel = ({ meta, onPick }) => {
  const ui = meta?.data?.ui;
  if (!ui || ui.type !== "rooms_list" || !Array.isArray(ui.rooms) || ui.rooms.length === 0) return null;

  const adults = Number(meta?.entities?.adults ?? ui?.adults ?? 1) || 1;
  const checkIn = meta?.entities?.check_in_date || null;
  const checkOut = meta?.entities?.check_out_date || null;

  const headline = `${formatDateHuman(checkIn)} → ${formatDateHuman(checkOut)} • ${adults} adult${adults > 1 ? "s" : ""}`;

  const getRoomImage = (room) => {
    const path = room?.image;
    if (!path) return null;
    return isAbsUrl(path) ? path : joinUrl(ROOM_ASSET_BASE, path);
  };

  const priceForAdults = (room) => {
    const single = room?.rate_single;
    const dbl = room?.rate_double;
    if (adults <= 1) return { price: single ?? dbl, mode: "Single", other: dbl ?? null };
    return { price: dbl ?? single, mode: "Double", other: single ?? null };
  };

  return (
    <div className="panel rooms-panel">
      <div className="panel-header">
        <div className="panel-title">Available Rooms</div>
        <div className="panel-count">{ui.rooms.length}</div>
      </div>

      <div className="rooms-subheader">{headline}</div>

      <div className="rooms-grid">
        {ui.rooms.map((r) => {
          const img = getRoomImage(r);
          const available = Number(r.available_rooms ?? 0);
          const soldOut = available <= 0;
          const p = priceForAdults(r);
          const roomName = r.type_name || "Room";

          return (
            <div key={r.room_type_id ?? r.index ?? roomName} className={`room-card ${soldOut ? "is-soldout" : ""}`}>
              <div className="room-media">
                {img ? (
                  <img className="room-img" src={img} alt={roomName} loading="lazy" />
                ) : (
                  <div className="room-img room-img-fallback" aria-hidden="true" />
                )}

                <div className={`room-stock ${soldOut ? "danger" : "ok"}`}>
                  {soldOut ? "Sold out" : `${available} available`}
                </div>
              </div>

              <div className="room-body">
                <div className="room-title-row">
                  <div className="room-title">{roomName}</div>
                  <div className="room-max">Max {r.max_occupancy ?? "—"}</div>
                </div>

                <div className="room-price-row">
                  <div className="room-price">
                    <span className="room-price-main">{formatINR(p.price)}</span>
                    <span className="room-price-suffix">/night</span>
                  </div>
                  <div className="room-occupancy">{p.mode} occupancy</div>
                </div>

                {p.other != null && (
                  <div className="room-price-alt">Other: {formatINR(p.other)} ({p.mode === "Single" ? "Double" : "Single"})</div>
                )}

                <div className="room-cta">
                  <button
                    type="button"
                    className="btn btn-primary room-select"
                    disabled={soldOut}
                    onClick={() => onPick(r.index != null ? String(r.index) : roomName)}
                  >
                    Select {r.index ? `#${r.index}` : "room"}
                  </button>
                </div>

                <div className="room-cta-secondary">
                  <button
                    type="button"
                    className="btn btn-outline room-secondary"
                    disabled={soldOut}
                    onClick={() => onPick(r.index != null ? String(r.index) : roomName)}
                    aria-label={`Tap to select ${roomName}`}
                  >
                    Tap to select
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {Array.isArray(ui.quick_replies) && ui.quick_replies.length > 0 && (
        <div className="room-quick-replies">
          {ui.quick_replies.map((q) => (
            <button key={String(q)} type="button" className="chip-btn" onClick={() => onPick(String(q))}>
              {q}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const MessageBubble = ({ message, userProfilePic, onAction }) => {
  const [showDevDetails, setShowDevDetails] = useState(false);

  const isUser = message.role === "user";
  const isTyping = message._typing;

  const meta = message.meta || null;
  const intent = String(meta?.intent || "").toLowerCase();
  const stage = String(meta?.stage || "");
  const uiType = String(meta?.data?.ui?.type || "");

  const showRoomsList = !isUser && !isTyping && uiType === "rooms_list";

  const bookingsList =
    Array.isArray(meta?.data) ? meta.data :
    Array.isArray(meta?.data?.bookings) ? meta.data.bookings :
    [];
  const showBookings =
    !isUser && !isTyping && intent === "search" && bookingsList.length > 0;

  const showPreview =
    !isUser &&
    !isTyping &&
    !!meta &&
    meta?.action_executed === false &&
    (meta?.data?.ui?.preview === true || stage === "AWAITING_CONFIRM");

  const hasBookingSummary =
    !!meta?.data?.summary ||
    !!meta?.data?.bookingNumber ||
    !!meta?.data?.bookingId ||
    !!meta?.data?.booking?.summary;

  const showBookingConfirmed =
    !isUser &&
    !isTyping &&
    intent === "book" &&
    meta?.action_executed === true &&
    hasBookingSummary;

  const displayText = useMemo(() => {
    if (isTyping) return message.text;
    if (!isUser && meta) {
      if (showBookingConfirmed) return "✅ Booking confirmed.";
      if (showPreview) return "🧾 Please review your booking details:";
      if (showRoomsList) return "Here are the available rooms:";
      if (showBookings) return "Here are your current bookings:";
    }
    return message.text;
  }, [isTyping, message.text, isUser, meta, showRoomsList, showPreview, showBookingConfirmed, showBookings]);

  const hasRichUI = !isUser && !isTyping && !!meta && (
    showRoomsList || showPreview || showBookings || showBookingConfirmed
  );

  const hideBubbleText =
    hasRichUI &&
    !isTyping &&
    !isUser &&
    [
      "Here are the available rooms:",
      "🧾 Please review your booking details:",
      "Here are your current bookings:",
      "✅ Booking confirmed.",
    ].includes(String(displayText || "").trim());

  const bubbleClass = hasRichUI
    ? `bubble ${isUser ? "user-bubble" : "bot-bubble"} bubble-compact`
    : `bubble ${isUser ? "user-bubble" : "bot-bubble"}`;

  return (
    <div className={`message-row ${isUser ? "user-row" : "bot-row"}`}>
      {!isUser && message.agent && (
        <img src={message.agent.image} alt={message.agent.name} className="message-avatar" />
      )}

      <div className="message-container">
        <div className={bubbleClass}>
          {isTyping ? (
            <div className="typing-wrapper">
              <div className="typing-name">{message.agent?.name || "Assistant"} is typing</div>
              <div className="typing-dots">
                <span></span><span></span><span></span>
              </div>
            </div>
          ) : (
            !hideBubbleText && <div className="bubble-content">{displayText}</div>
          )}

          <div className="bubble-time">{formatTimestamp(message.ts)}</div>
        </div>

        {!isUser && !isTyping && meta && (
          <div className="rich-block">
            {showPreview && <BookingPreviewPanel meta={meta} onAction={onAction} />}
            {showRoomsList && <RoomsListPanel meta={meta} onPick={onAction} />}
            {showBookings && <BookingsPanel data={bookingsList} onAction={onAction} />}
            {showBookingConfirmed && <BookingConfirmedPanel meta={meta} onAction={onAction} />}

            {process.env.NODE_ENV === "development" && (
              <div className="dev-details-toggle mt-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setShowDevDetails((v) => !v)}
                >
                  {showDevDetails ? "Hide" : "Show"} metadata
                </button>
                {showDevDetails && <pre className="dev-json">{JSON.stringify(meta, null, 2)}</pre>}
              </div>
            )}
          </div>
        )}
      </div>

      {isUser && (
        <img src={userProfilePic} alt="You" className="message-avatar user-avatar" />
      )}
    </div>
  );
};

// ──────────────────────────────────────────────
// Main Chatbot Component
// ──────────────────────────────────────────────
const Chatbot = ({
  isOpen,
  toggleChatbot,
  apiURL,
  code,
  themeColor,
  size, // { width, height }
  poweredBy = true,
  aiBot, // ✅ NEW: received from UMD index.js
}) => {
  const userProfilePic = "https://netshine-assets.blr1.cdn.digitaloceanspaces.com/app-assets/avatar-1.jpg";
  const apiUrl = useMemo(() => String(apiURL || "").trim(), [apiURL]);

  // Normalize aiBot config safely
  const aiBotSafe = useMemo(() => (isPlainObject(aiBot) ? aiBot : {}), [aiBot]);

  const aiBotEnabled = useMemo(() => {
    // Backward compatible: if aiBot is not provided, keep widget usable (treat as enabled)
    if (!isPlainObject(aiBotSafe) || Object.keys(aiBotSafe).length === 0) return true;
    return aiBotSafe.enabled === true;
  }, [aiBotSafe]);

  const aiAssistantNames = useMemo(() => {
    const list = Array.isArray(aiBotSafe.assistantNameList) ? aiBotSafe.assistantNameList : [];
    if (list.length) return list.map((s) => String(s || "").trim()).filter(Boolean);

    const fromStr = parseCommaNames(aiBotSafe.assistantNames);
    return fromStr;
  }, [aiBotSafe]);

  const quickActionKeys = useMemo(() => {
    // Prefer keys array
    if (Array.isArray(aiBotSafe.quickActionKeys)) return sanitizeKeys(aiBotSafe.quickActionKeys);
    // Support boolean map format too
    if (isPlainObject(aiBotSafe.quickActions)) {
      return sanitizeKeys(Object.keys(aiBotSafe.quickActions).filter((k) => aiBotSafe.quickActions[k] === true));
    }
    // Backward fallback: show default quick actions
    return sanitizeKeys(ALLOWED_QA);
  }, [aiBotSafe]);

  const quickActionsEnabled = useMemo(() => {
    const show = aiBotSafe.showQuickActions !== false;
    return aiBotEnabled && show && quickActionKeys.length > 0;
  }, [aiBotEnabled, aiBotSafe, quickActionKeys]);

  // Resolve agent (name from aiBot list; fallback to SUPPORT_AGENTS)
  const [assignedAgent] = useState(() => {
    const chosenName =
      pickOneRandom(aiAssistantNames) ||
      pickOneRandom(SUPPORT_AGENTS.map((a) => a.name)) ||
      "Assistant";

    return {
      name: chosenName,
      image: resolveAgentImage(chosenName),
    };
  });

  const inputRef = useRef(null);

  const [sessionId, setSessionId] = useState(() => {
    const saved = localStorage.getItem(SESSION_ID_KEY);
    if (saved) return saved;
    const fresh = newSessionId();
    localStorage.setItem(SESSION_ID_KEY, fresh);
    return fresh;
  });

  const sessionIdRef = useRef(sessionId);
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);

  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem(SESSION_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length) return parsed;
      } catch {}
    }
    return [{
      id: `init_${Date.now()}`,
      role: "assistant",
      text: buildWelcomeText(assignedAgent.name, quickActionKeys),
      agent: assignedAgent,
      ts: Date.now(),
    }];
  });

  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  const messagesEndRef = useRef(null);
  const chatbotRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Voice (STT + TTS)
  const speechRecognitionRef = useRef(null);
  const speechSupported = useMemo(() => !!getSpeechRecognitionCtor(), []);
  const ttsSupported = useMemo(
    () =>
      typeof window !== "undefined" &&
      "speechSynthesis" in window &&
      typeof SpeechSynthesisUtterance !== "undefined",
    []
  );

  const [isListening, setIsListening] = useState(false);
  const [voiceError, setVoiceError] = useState("");
  const [voiceInterim, setVoiceInterim] = useState("");
  const [voiceAutoSend] = useState(true); // auto-send after speech end (can be exposed as a toggle later)
  const [botVoiceEnabled, setBotVoiceEnabled] = useState(false);

  const [callModeEnabled, setCallModeEnabled] = useState(false);
  const callModeRestartTimerRef = useRef(null);
  const sendMessageRef = useRef(null);

  const isOpenRef = useRef(isOpen);
  const isMinimizedRef = useRef(isMinimized);
  const isSendingRef = useRef(isSending);
  const otpPendingRef = useRef(false);
  const callModeEnabledRef = useRef(false);

  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);
  useEffect(() => { isMinimizedRef.current = isMinimized; }, [isMinimized]);
  useEffect(() => { isSendingRef.current = isSending; }, [isSending]);
  useEffect(() => { callModeEnabledRef.current = callModeEnabled; }, [callModeEnabled]);

  useEffect(() => {
    if (isOpen) localStorage.setItem(SESSION_KEY, JSON.stringify(messages));
  }, [messages, isOpen]);

  useEffect(() => {
    if (!isOpen) setIsMinimized(false);
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const handler = (e) => {
      if (isMinimized) return;
      if (chatbotRef.current && !chatbotRef.current.contains(e.target) && isOpen) {
        toggleChatbot();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen, isMinimized, toggleChatbot]);

  const handleMinimizeToggle = (e) => {
    e?.stopPropagation?.();
    setIsMinimized((v) => !v);
    touchActivity();
  };

  const lastMeta = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant" && messages[i].meta) return messages[i].meta;
    }
    return null;
  }, [messages]);

  const otpPending = useMemo(() => {
    const missing = Array.isArray(lastMeta?.missing_fields) ? lastMeta.missing_fields : [];
    const stage = String(lastMeta?.stage || "");
    return missing.includes("email_otp") || stage === "AWAITING_EMAIL_OTP";
  }, [lastMeta]);
  useEffect(() => { otpPendingRef.current = otpPending; }, [otpPending]);

  const shouldShowQuickActions = useMemo(() => {
    const isFirstScreen = messages.length <= 1;
    const afterReset = !!lastMeta?.journey_reset;
    // Hide quick actions while OTP is pending to avoid confusing taps
    return (isFirstScreen || afterReset) && !isSending && !otpPending;
  }, [messages.length, lastMeta?.journey_reset, isSending, otpPending]);

  const showQuickActions = shouldShowQuickActions && quickActionsEnabled;

  const voiceLang = useMemo(() => "en-IN", []);

  const formatBotReply = useCallback((result) => {
    if (!result) return "Sorry, I couldn't process that request.";
    if (result.success === false) return result.response || result.error || "An error occurred.";
    if (result.response?.trim()) return result.response;
    return "Understood. How else may I assist you?";
  }, []);

  const rotateSession = useCallback(({ clearMessages = false, reason = "NEW_FLOW" } = {}) => {
    const freshId = newSessionId();
    sessionIdRef.current = freshId;
    setSessionId(freshId);
    localStorage.setItem(SESSION_ID_KEY, freshId);

    if (clearMessages) {
      localStorage.removeItem(SESSION_KEY);
      setMessages([{
        id: `reset_${Date.now()}`,
        role: "assistant",
        text: reason === "IDLE"
          ? `${getGreeting()}, ${getUsername()}!\nSession timed out due to inactivity.\n\nHow may I help you today?`
          : buildWelcomeText(assignedAgent.name, quickActionKeys),
        agent: assignedAgent,
        ts: Date.now(),
      }]);
    }

    touchActivity();
  }, [assignedAgent, quickActionKeys]);

  const fetchChatResponse = useCallback(async (userText) => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const tokenNow = localStorage.getItem("token") || "";

    const res = await fetch(joinUrl(apiUrl, BOT_ENDPOINT), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(tokenNow ? { Authorization: `Bearer ${tokenNow}` } : {}),
      },
      signal: controller.signal,
      body: JSON.stringify({
        message: userText,
        session_id: sessionIdRef.current,
        org_id: code,
      }),
    });

    const json = await res.json().catch(() => null);
    if (!res.ok) {
      const err = new Error(json?.error || `HTTP ${res.status}`);
      err.status = res.status;
      throw err;
    }
    return json;
  }, [apiUrl, code]);

  const focusInputSoon = useCallback((delay = 0) => {
    window.setTimeout(() => {
      try { inputRef.current?.focus(); } catch {}
    }, delay);
  }, []);


  const clearCallModeRestart = useCallback(() => {
    if (callModeRestartTimerRef.current) {
      window.clearTimeout(callModeRestartTimerRef.current);
      callModeRestartTimerRef.current = null;
    }
  }, []);

  const scheduleCallModeResume = useCallback((delay = 650) => {
    clearCallModeRestart();

    if (!callModeEnabledRef.current) return;
    if (!speechSupported) return;
    if (!isOpenRef.current || isMinimizedRef.current) return;
    if (isSendingRef.current) return;
    if (otpPendingRef.current) return;
    if (isListening) return;

    callModeRestartTimerRef.current = window.setTimeout(() => {
      callModeRestartTimerRef.current = null;
      if (!callModeEnabledRef.current) return;
      if (!isOpenRef.current || isMinimizedRef.current) return;
      if (isSendingRef.current || otpPendingRef.current) return;
      if (speechRecognitionRef.current && isListening) return;
      try { startListening(); } catch {}
    }, delay);
  }, [clearCallModeRestart, speechSupported, isListening]);


  const stopBotSpeech = useCallback(() => {
    try {
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    } catch {}
  }, []);

  const shouldSpeakReply = useCallback((text) => {
    const t = String(text || "").toLowerCase();
    if (!t) return false;
    // Avoid reading OTP prompts/codes out loud by default
    if (/\botp\b|one[- ]time password|verification code/.test(t)) return false;
    return true;
  }, []);

  const speakBotText = useCallback((text) => {
    if (!botVoiceEnabled || !ttsSupported) return false;
    const toSpeak = cleanTextForSpeech(text);
    if (!toSpeak || !shouldSpeakReply(toSpeak)) return false;

    try {
      stopBotSpeech();

      const utter = new SpeechSynthesisUtterance(toSpeak);
      utter.lang = voiceLang;
      utter.rate = 1;
      utter.pitch = 1;
      utter.volume = 1;

      const voices = window.speechSynthesis?.getVoices?.() || [];
      const preferred =
        voices.find((v) => /en[-_]?IN/i.test(v.lang)) ||
        voices.find((v) => /en/i.test(v.lang)) ||
        null;
      if (preferred) utter.voice = preferred;

      utter.onend = () => {
        if (callModeEnabledRef.current) scheduleCallModeResume(350);
      };
      utter.onerror = () => {
        if (callModeEnabledRef.current) scheduleCallModeResume(350);
      };

      window.speechSynthesis.speak(utter);
      return true;
    } catch (e) {
      console.warn("TTS error:", e);
      return false;
    }
  }, [botVoiceEnabled, ttsSupported, shouldSpeakReply, stopBotSpeech, voiceLang, scheduleCallModeResume]);

  const stopListening = useCallback(() => {
    clearCallModeRestart();
    try { speechRecognitionRef.current?.stop?.(); } catch {}
    setIsListening(false);
  }, [clearCallModeRestart]);

  const startListening = useCallback(() => {
    setVoiceError("");

    if (!speechSupported) {
      setVoiceError("Voice input works best in Chrome or Edge.");
      return;
    }

    if (otpPending) {
      setVoiceError("Voice input is disabled during OTP entry.");
      return;
    }

    try {
      stopBotSpeech();
      const SpeechRecognitionCtor = getSpeechRecognitionCtor();
      if (!SpeechRecognitionCtor) {
        setVoiceError("Speech recognition is unavailable.");
        return;
      }

      const recognition = new SpeechRecognitionCtor();
      speechRecognitionRef.current = recognition;

      recognition.lang = voiceLang;
      recognition.interimResults = true;
      recognition.continuous = false;
      recognition.maxAlternatives = 1;

      let finalText = "";

      recognition.onstart = () => {
        clearCallModeRestart();
        setIsListening(true);
        setVoiceInterim("");
      };

      recognition.onresult = (event) => {
        let interim = "";
        let finalChunk = "";

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i]?.[0]?.transcript || "";
          if (event.results[i].isFinal) finalChunk += transcript;
          else interim += transcript;
        }

        if (finalChunk) {
          finalText = `${finalText} ${finalChunk}`.trim();
          setInput(finalText);
        }
        setVoiceInterim(interim.trim());
      };

      recognition.onerror = (event) => {
        const code = event?.error || "unknown";
        if (code === "aborted") return;
        if (code === "not-allowed") {
          setVoiceError("Microphone permission denied.");
          if (callModeEnabledRef.current) setCallModeEnabled(false);
        } else if (code === "no-speech") {
          setVoiceError(callModeEnabledRef.current ? "Listening..." : "No speech detected. Please try again.");
        } else {
          setVoiceError("Voice input failed. Please try again.");
        }
      };

      recognition.onend = () => {
        setIsListening(false);
        setVoiceInterim("");

        const finalTranscript = String(finalText || "").trim();
        const shouldAutoSendNow = (voiceAutoSend || callModeEnabledRef.current) && finalTranscript;

        if (shouldAutoSendNow && !isSendingRef.current) {
          setInput(finalTranscript);
          window.setTimeout(() => {
            sendMessageRef.current?.(finalTranscript);
          }, 0);
          return;
        }

        if (finalTranscript) {
          setInput(finalTranscript);
        }

        if (callModeEnabledRef.current && !finalTranscript) {
          scheduleCallModeResume(450);
        } else {
          focusInputSoon(0);
        }
      };

      recognition.start();
    } catch (e) {
      console.error("SpeechRecognition start error:", e);
      setIsListening(false);
      setVoiceError("Unable to start voice input.");
    }
  }, [
    speechSupported,
    otpPending,
    voiceLang,
    voiceAutoSend,
    stopBotSpeech,
    focusInputSoon,
    setInput,
    clearCallModeRestart,
    scheduleCallModeResume,
  ]);

  const sendMessage = useCallback(async (text) => {
    const msg = String(text || "").trim();
    if (!msg || isSending) return;

    stopBotSpeech();
    if (isListening) stopListening();

    touchActivity();
    setInput("");
    focusInputSoon(0);

    setMessages((prev) => [
      ...prev,
      { id: `user_${Date.now()}`, role: "user", text: msg, ts: Date.now() },
    ]);

    const typingMsgId = `typing_${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: typingMsgId, role: "assistant", text: "Thinking...", agent: assignedAgent, ts: Date.now(), _typing: true },
    ]);

    setIsSending(true);

    try {
      const result = await fetchChatResponse(msg);
      const replyText = formatBotReply(result);

      setMessages((prev) =>
        prev
          .filter((m) => m.id !== typingMsgId)
          .concat([{ id: `bot_${Date.now()}`, role: "assistant", text: replyText, agent: assignedAgent, ts: Date.now(), meta: result }])
      );

      const didSpeak = speakBotText(replyText);
      if (!didSpeak) scheduleCallModeResume(350);
      touchActivity();

      if (result?.journey_reset) {
        rotateSession({ clearMessages: false, reason: "COMPLETED" });
      }
    } catch (err) {
      console.error("Chat error:", err);
      const status = err?.status;
      const isAuthError = status === 401 || status === 403;

      const errText = isAuthError
        ? "Your session has expired. Please sign in again."
        : "Sorry — I couldn't connect right now. Please try again.";

      setMessages((prev) =>
        prev
          .filter((m) => m.id !== typingMsgId)
          .concat([{
            id: `err_${Date.now()}`,
            role: "assistant",
            text: errText,
            agent: assignedAgent,
            ts: Date.now(),
          }])
      );

      const didSpeakErr = speakBotText(errText);
      if (!didSpeakErr) scheduleCallModeResume(450);
    } finally {
      setIsSending(false);
      focusInputSoon(30);
    }
  }, [assignedAgent, isSending, fetchChatResponse, formatBotReply, rotateSession, focusInputSoon, stopBotSpeech, isListening, stopListening, speakBotText, scheduleCallModeResume]);

  useEffect(() => { sendMessageRef.current = sendMessage; }, [sendMessage]);

  const handleQuickClick = useCallback(async (text) => {
    rotateSession({ clearMessages: true, reason: "QUICK_ACTION" });
    await sendMessage(text);
  }, [rotateSession, sendMessage]);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleClose = () => {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(SESSION_ID_KEY);

    setMessages([{
      id: `fresh_${Date.now()}`,
      role: "assistant",
      text: buildWelcomeText(assignedAgent.name, quickActionKeys),
      agent: assignedAgent,
      ts: Date.now(),
    }]);

    const fresh = newSessionId();
    setSessionId(fresh);
    localStorage.setItem(SESSION_ID_KEY, fresh);

    setCallModeEnabled(false);
    clearCallModeRestart();
    stopListening();
    stopBotSpeech();
    setIsMinimized(false);
    toggleChatbot();
  };

  useEffect(() => {
    if (!isOpen) return;
    if (!isSending && !isMinimized) focusInputSoon(0);
  }, [isSending, isOpen, isMinimized, focusInputSoon]);

  useEffect(() => {
    if (!isOpen || isMinimized) {
      stopListening();
      stopBotSpeech();
    }
  }, [isOpen, isMinimized, stopListening, stopBotSpeech]);

  useEffect(() => {
    if (otpPending && callModeEnabled) {
      setCallModeEnabled(false);
      clearCallModeRestart();
      stopListening();
    }
  }, [otpPending, callModeEnabled, clearCallModeRestart, stopListening]);

  useEffect(() => () => {
    try { speechRecognitionRef.current?.stop?.(); } catch {}
    try { window.speechSynthesis?.cancel?.(); } catch {}
    if (callModeRestartTimerRef.current) window.clearTimeout(callModeRestartTimerRef.current);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    touchActivity();

    const interval = window.setInterval(() => {
      const last = getLastActive();
      if (!last) return;

      const timeout = otpPending ? OTP_GRACE_MS : IDLE_RESET_MS;
      if (Date.now() - last > timeout) {
        localStorage.removeItem(SESSION_KEY);
        const newId = newSessionId();
        setSessionId(newId);
        localStorage.setItem(SESSION_ID_KEY, newId);

        setMessages([{
          id: `timeout_${Date.now()}`,
          role: "assistant",
          text: `${getGreeting()}, ${getUsername()}!\nSession timed out due to inactivity.\n\nHow may I help you today?`,
          agent: assignedAgent,
          ts: Date.now(),
        }]);

        touchActivity();
      }
    }, 10000);

    return () => window.clearInterval(interval);
  }, [isOpen, otpPending, assignedAgent]);

  const containerStyle = useMemo(() => {
    const st = {};
    if (themeColor) st["--primary"] = String(themeColor);
    if (size?.width) st["--chat-w"] = typeof size.width === "number" ? `${size.width}px` : String(size.width);
    if (size?.height) st["--chat-h"] = typeof size.height === "number" ? `${size.height}px` : String(size.height);
    return st;
  }, [themeColor, size]);


  const toggleCallMode = useCallback(() => {
    if (!speechSupported || otpPending) return;

    setVoiceError("");
    setCallModeEnabled((prev) => {
      const next = !prev;
      if (!next) {
        clearCallModeRestart();
        stopListening();
        stopBotSpeech();
        focusInputSoon(0);
      } else {
        stopBotSpeech();
        clearCallModeRestart();
        if (!isSending && !isListening && isOpen && !isMinimized) {
          window.setTimeout(() => {
            if (!isSendingRef.current && !otpPendingRef.current && isOpenRef.current && !isMinimizedRef.current) {
              try { startListening(); } catch {}
            }
          }, 120);
        }
      }
      return next;
    });
    touchActivity();
  }, [
    speechSupported,
    otpPending,
    clearCallModeRestart,
    stopListening,
    stopBotSpeech,
    focusInputSoon,
    isSending,
    isListening,
    isOpen,
    isMinimized,
    startListening,
  ]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!isSending && input.trim()) sendMessage(input);
      focusInputSoon(0);
    }
  };

  // If bot is explicitly disabled but still mounted somehow, render nothing safely
  if (!aiBotEnabled) return null;

  return (
    <div
      ref={chatbotRef}
      className={`enterprise-chat-container ${isOpen ? "open" : "closed"} ${isMinimized ? "minimized" : ""}`}
      aria-live="polite"
      style={containerStyle}
    >
      <div
        className="chat-header"
        role={isMinimized ? "button" : undefined}
        tabIndex={isMinimized ? 0 : undefined}
        onClick={isMinimized ? () => setIsMinimized(false) : undefined}
        onKeyDown={isMinimized ? (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsMinimized(false);
          }
        } : undefined}
        aria-label={isMinimized ? "Expand chat" : undefined}
      >
        <div className="agent-info">
          <div className="agent-avatar-container">
            <img
              src={assignedAgent.image}
              alt={assignedAgent.name}
              className="agent-avatar"
            />
            <span className="online-dot" />
          </div>
          <div className="agent-details">
            <h6 className="agent-name">{assignedAgent.name}</h6>
            <span className="agent-role">AI Front Office Assistant</span>
          </div>
        </div>

        <div className="chat-header-actions">
          <button
            type="button"
            className={`call-mode-btn ${callModeEnabled ? "active" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleCallMode();
            }}
            disabled={!speechSupported || otpPending}
            aria-label={callModeEnabled ? "Turn off call mode" : "Turn on call mode"}
            title={
              !speechSupported
                ? "Call mode works best in Chrome/Edge"
                : otpPending
                ? "Call mode unavailable during OTP entry"
                : callModeEnabled
                ? "End call mode"
                : "Start call mode"
            }
          >
            <FaPhone />
            <span className="call-mode-label">{callModeEnabled ? "On" : "Call"}</span>
          </button>

          <button
            type="button"
            className={`voice-toggle-btn ${botVoiceEnabled ? "active" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              setBotVoiceEnabled((prev) => {
                const next = !prev;
                if (!next) stopBotSpeech();
                return next;
              });
            }}
            aria-label={botVoiceEnabled ? "Turn off bot voice" : "Turn on bot voice"}
            title={botVoiceEnabled ? "Bot voice on" : "Bot voice off"}
          >
            {botVoiceEnabled ? <FaVolumeUp /> : <FaVolumeMute />}
          </button>

          <button
            type="button"
            className="minimize-btn"
            onClick={handleMinimizeToggle}
            aria-label={isMinimized ? "Expand" : "Minimize"}
            title={isMinimized ? "Expand" : "Minimize"}
          >
            <FaChevronDown className={isMinimized ? "chev up" : "chev"} />
          </button>

          <button
            type="button"
            className="close-btn"
            onClick={handleClose}
            aria-label="Close chat"
          >
            ×
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          <div className="messages-scroll-area">
            {messages.map((msg) => (
              <MessageBubble
                key={msg.id}
                message={msg}
                userProfilePic={userProfilePic}
                onAction={sendMessage}
              />
            ))}

            {showQuickActions && (
              <QuickActions
                onPick={handleQuickClick}
                disabled={isSending}
                actionKeys={quickActionKeys}
              />
            )}

            <div ref={messagesEndRef} />
          </div>

          <form className="chat-input-area" onSubmit={handleSubmit}>
            <div className="input-row">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={otpPending ? "Enter verification code..." : isSending ? "Processing..." : "Type your message..."}
                disabled={isSending}
                className="chat-input"
                autoComplete="off"
                inputMode={otpPending ? "numeric" : "text"}
                enterKeyHint="send"
              />

              <button
                type="button"
                className={`mic-btn ${isListening ? "listening" : ""}`}
                onClick={isListening ? stopListening : startListening}
                disabled={isSending || otpPending || !speechSupported}
                aria-label={isListening ? "Stop listening" : "Start voice input"}
                title={
                  !speechSupported
                    ? "Voice input works best in Chrome/Edge"
                    : otpPending
                    ? "Voice disabled during OTP entry"
                    : isListening
                    ? "Stop listening"
                    : "Start voice input"
                }
              >
                {isListening ? <FaStop /> : <FaMicrophone />}
              </button>

              <button
                type="submit"
                className="send-btn"
                disabled={isSending || !input.trim()}
                aria-label="Send message"
              >
                {isSending ? <FaSpinner className="spin" /> : <FaPaperPlane />}
              </button>
            </div>

            <div className="input-hint">
              <span className="input-status-left">
                {isListening
                  ? `Listening${voiceInterim ? `: ${voiceInterim}` : "…"}${callModeEnabled ? " (Call mode)" : ""}`
                  : voiceError
                  ? voiceError
                  : callModeEnabled
                  ? "Call mode active — speak naturally, I’ll send automatically."
                  : isSending
                  ? "Processing your request…"
                  : "Press Enter to send"}
              </span>
              {!isSending && !isListening && <span><kbd>Enter</kbd></span>}
              {poweredBy && (
                <span className="powered-by">
                  Powered by{" "}
                  <a href="https://netshine.one" target="_blank" rel="noreferrer">
                    NetShine ONE
                  </a>
                </span>
              )}
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default Chatbot;
